import clsx from "clsx";
import {
  DOCS_FORM_NAMES,
  FUNCTION_DIAGNOSIS_PRIORITIES,
  MUSCOSKELETAL_RESILIENCE,
  NUMBER_OF_OPS_FIELDS,
  OPERATION_INDEXES,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_UNKNOWN,
  PREDICAMENT_STATE_YES,
  REHAB_ADMISSION,
  REHAB_DIRECTIVE_TYPE,
  REHAB_FOCUS,
  SPECIAL_REQUIREMENTS,
  TRANSPORTATION_SUBTYPE,
  TRANSPORTATION_TYPE,
  TYPE_OF_REHAB,
} from "core/consts";
import {
  ICD_CODE_CHARACTER_LIMIT,
  INFECTIONS_CHARACTER_LIMIT,
} from "core/schemas/shared";
import { isFilled } from "core/schemas/utils";
import { GKVMedicalFormData } from "core/types";
import {
  Button,
  ControlledCheckboxGroup,
  ControlledDateInput,
  ControlledInput,
  ControlledRadioGroup,
  ControlledSelect,
  ControlledTextarea,
  Input,
} from "ds/ui";
import {
  SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS,
  useICDReasonOptions,
} from "dsl/ecosystems/PatientForms/RehabForms/GKVMedicalForm/hooks";
import { useVisibleCount } from "dsl/hooks";
import { PlusIcon, TrashIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import Translations from "translations/types";
import { DEFAULT_OPS } from ".";
import { FormFieldConfigProps } from "..";
import { SourceWrapper } from "../../utils";

export const RehabAdmissionTypeField = ({
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const rehabAdmissionType = useWatch({ name: "rehab_admission_type" });

  useEffect(() => {
    if (rehabAdmissionType !== REHAB_ADMISSION.OUTSIDE_TWO_WEEKS) {
      setValue("rehab_admission_outside_two_weeks_explanation", "");
      setValue("rehab_admission_outside_two_weeks_start_date", null);
    }
    if (rehabAdmissionType !== REHAB_ADMISSION.WITHIN_TWO_WEEKS) {
      setValue("rehab_admission_within_two_weeks_start_date", null);
    }
  }, [rehabAdmissionType, setValue]);

  const options = [
    {
      label: translations.patientForms.medicalForm.sectionOne.optionDirect,
      value: REHAB_ADMISSION.DIRECT,
    },
    {
      label: translations.patientForms.medicalForm.sectionOne.withinTwoWeeks,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName="rehab_admission_within_two_weeks_start_date"
          isIconOnly
          setSourceSelected={setSourceSelected}
          sourceSelected={sourceSelected}
        >
          <ControlledDateInput
            label={
              translations.patientForms.medicalForm.sectionOne
                .earliestDatePossible
            }
            name="rehab_admission_within_two_weeks_start_date"
          />
        </SourceWrapper>
      ),
      value: REHAB_ADMISSION.WITHIN_TWO_WEEKS,
    },
    {
      label: translations.patientForms.medicalForm.sectionOne.outsideTwoWeeks,
      subForm: () => (
        <>
          <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
            fieldName="rehab_admission_outside_two_weeks_start_date"
            isIconOnly
            setSourceSelected={setSourceSelected}
            sourceSelected={sourceSelected}
          >
            <ControlledDateInput
              label={
                translations.patientForms.medicalForm.sectionOne
                  .earliestDatePossible
              }
              name="rehab_admission_outside_two_weeks_start_date"
            />
          </SourceWrapper>
          <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
            fieldName="rehab_admission_outside_two_weeks_explanation"
            isIconOnly
            setSourceSelected={setSourceSelected}
            sourceSelected={sourceSelected}
          >
            <ControlledTextarea
              label={
                translations.patientForms.medicalForm.sectionOne.medicalReasons
              }
              name="rehab_admission_outside_two_weeks_explanation"
            />
          </SourceWrapper>
        </>
      ),
      value: REHAB_ADMISSION.OUTSIDE_TWO_WEEKS,
    },
  ];

  return (
    <ControlledRadioGroup
      aria-label={
        translations.patientForms.generalForm.sectionI.accessibilityGuardianType
      }
      isRequired={!skipRequiredValidation}
      name="rehab_admission_type"
      options={options}
    />
  );
};

export const InfectionsStateField = ({
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const infectionsState = useWatch({ name: "infections_state" });

  useEffect(() => {
    if (infectionsState !== PREDICAMENT_STATE_YES) {
      setValue("infections", "");
    }
  }, [infectionsState, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName="infections"
          isIconOnly
          setSourceSelected={setSourceSelected}
          sourceSelected={sourceSelected}
        >
          <ControlledTextarea
            label={translations.patientForms.medicalForm.sectionTwo.typeOfGerms}
            maxLength={INFECTIONS_CHARACTER_LIMIT}
            name="infections"
          />
        </SourceWrapper>
      ),
      value: PREDICAMENT_STATE_YES,
    },
    {
      label: translations.patientForms.medicalForm.sectionTwo.germsUnknown,
      value: PREDICAMENT_STATE_UNKNOWN,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      isRequired={!skipRequiredValidation}
      label={translations.patientForms.medicalForm.sectionTwo.germs}
      name="infections_state"
      options={options}
    />
  );
};

export const ComplicationInTreatmentProcessField = ({
  auction,
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const complicationInTreatmentProcess = useWatch({
    name: "complication_in_treatment_process",
  });

  useEffect(() => {
    if (complicationInTreatmentProcess !== PREDICAMENT_STATE_YES) {
      setValue("complication_cause_maindiagnosis_description", "");
      setValue("complication_cause_cardiovascular_description", "");
      setValue("complication_cause_other_description", "");
    }
  }, [complicationInTreatmentProcess, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <ComplicationsSubForm
          auction={auction}
          setSourceSelected={setSourceSelected}
          skipRequiredValidation={skipRequiredValidation}
          sourceSelected={sourceSelected}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      isRequired={!skipRequiredValidation}
      label={translations.patientForms.medicalForm.sectionTwo.complications}
      name="complication_in_treatment_process"
      options={options}
    />
  );
};

const ComplicationsSubForm = ({
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const complicationCauseMaindiagnosisDescription =
    useWatch<GKVMedicalFormData>({
      name: "complication_cause_maindiagnosis_description",
    });
  const complicationCauseCardiovascularDescription =
    useWatch<GKVMedicalFormData>({
      name: "complication_cause_cardiovascular_description",
    });
  const complicationCauseOtherDescription = useWatch<GKVMedicalFormData>({
    name: "complication_cause_other_description",
  });

  const complicationOptions = [
    {
      label:
        translations.patientForms.medicalForm.sectionTwo.relatedToDiagnosis,
      name: "complication_cause_maindiagnosis_description",
    },
    {
      label:
        translations.patientForms.medicalForm.sectionTwo.optionCardiovascular,
      name: "complication_cause_cardiovascular_description",
    },
    {
      label:
        translations.patientForms.medicalForm.sectionTwo
          .optionOtherComplication,
      name: "complication_cause_other_description",
    },
  ] as const;

  const allEmpty =
    !isFilled(complicationCauseMaindiagnosisDescription) &&
    !isFilled(complicationCauseCardiovascularDescription) &&
    !isFilled(complicationCauseOtherDescription);

  const isFieldFilled = (
    fieldName: (typeof complicationOptions)[number]["name"],
  ) => {
    switch (fieldName) {
      case "complication_cause_maindiagnosis_description":
        return isFilled(complicationCauseMaindiagnosisDescription);
      case "complication_cause_cardiovascular_description":
        return isFilled(complicationCauseCardiovascularDescription);
      case "complication_cause_other_description":
        return isFilled(complicationCauseOtherDescription);
    }
  };

  return (
    <>
      {complicationOptions.map(({ label, name }) => {
        const isRequired =
          !skipRequiredValidation && (allEmpty || isFieldFilled(name));

        return (
          <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
            key={name}
            fieldName={name as keyof GKVMedicalFormData}
            isIconOnly
            setSourceSelected={setSourceSelected}
            sourceSelected={sourceSelected}
          >
            <ControlledTextarea
              isRequired={isRequired}
              label={label}
              name={name}
            />
          </SourceWrapper>
        );
      })}
    </>
  );
};

export const RehaCapacityField = ({
  setSourceSelected,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const rehaCapacity = useWatch({ name: "reha_capacity" });

  useEffect(() => {
    if (rehaCapacity !== MUSCOSKELETAL_RESILIENCE.PARTIALLY) {
      setValue("reha_capacity_partial", null);
    }
  }, [rehaCapacity, setValue]);

  const options = [
    {
      label: translations.patientForms.medicalForm.sectionThree.fully,
      value: MUSCOSKELETAL_RESILIENCE.FULLY,
    },
    {
      label: translations.patientForms.medicalForm.sectionThree.partially,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName="reha_capacity_partial"
          isIconOnly
          setSourceSelected={setSourceSelected}
          sourceSelected={sourceSelected}
        >
          <ControlledInput
            name="reha_capacity_partial"
            placeholder=" "
            type="number"
          />
        </SourceWrapper>
      ),
      value: MUSCOSKELETAL_RESILIENCE.PARTIALLY,
    },
    {
      label: translations.patientForms.medicalForm.sectionThree.stable,
      value: MUSCOSKELETAL_RESILIENCE.STABLE,
    },
  ];

  return (
    <ControlledRadioGroup
      aria-label={
        translations.patientForms.medicalForm.sectionThree.musculoskeletalSystem
      }
      name="reha_capacity"
      options={options}
    />
  );
};

export const RehabFocusField = ({
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const rehabFocus = useWatch({ name: "rehab_focus" });

  useEffect(() => {
    if (rehabFocus !== REHAB_FOCUS.OTHER) {
      setValue("rehab_focus_other_description", "");
    }
  }, [rehabFocus, setValue]);

  const options = [
    {
      label: translations.patientForms.medicalForm.sectionFive.heart,
      value: REHAB_FOCUS.HEART,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.neurologicalDisease,
      value: REHAB_FOCUS.NEUROLOGICAL_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.bloodVessels,
      value: REHAB_FOCUS.BLOOD_VESSELS,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.oncologicalDiseases,
      value: REHAB_FOCUS.ONCOLOGICAL_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.inflammatoryDiseases,
      value: REHAB_FOCUS.INFLAMMATORY_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive
          .gynaecologicalDiseases,
      value: REHAB_FOCUS.GYNAECOLOGICAL_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.musculoskeletalSystem,
      value: REHAB_FOCUS.MUSCOLOSKELETAL_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.skinDiseases,
      value: REHAB_FOCUS.SKIN_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive
          .gastroenterologicalDiseases,
      value: REHAB_FOCUS.GASTROENTEROLOGICAL_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive
          .psychosomaticDisorders,
      value: REHAB_FOCUS.PSYCHOSOMATIC_DISORDER,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.endocrineDiseases,
      value: REHAB_FOCUS.ENDOCRINE_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.mentalIllnesses,
      value: REHAB_FOCUS.MENTAL_ILLNESS,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.respiratoryDiseases,
      value: REHAB_FOCUS.RESPIRATORY_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.addictiveDisorders,
      value: REHAB_FOCUS.ADDICTIVE_DISORDER,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.nephrologicalDiseases,
      value: REHAB_FOCUS.NEPHROLOGICAL_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.bloodDiseases,
      value: REHAB_FOCUS.BLOOD_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.urologicalDiseases,
      value: REHAB_FOCUS.UROLOGICAL_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.geriatrics,
      value: REHAB_FOCUS.GERIATRICS,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.others,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName="rehab_focus_other_description"
          isIconOnly
          setSourceSelected={setSourceSelected}
          sourceSelected={sourceSelected}
        >
          <ControlledTextarea
            label={
              translations.patientForms.medicalForm.sectionFive
                .otherTextfieldLabel
            }
            name="rehab_focus_other_description"
          />
        </SourceWrapper>
      ),
      value: REHAB_FOCUS.OTHER,
    },
  ];

  return (
    <ControlledRadioGroup
      aria-label={translations.patientForms.medicalForm.sectionFive.rehabFocus}
      isRequired={!skipRequiredValidation}
      name="rehab_focus"
      options={options}
    />
  );
};

export const RecommendedRehaTypeField = ({
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const recommendedRehaType = useWatch({ name: "recommended_reha_type" });

  const options = [
    {
      label: translations.patientForms.medicalForm.sectionFive.mobile,
      value: TYPE_OF_REHAB.AMBULANT,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.mobileOutpatient,
      value: TYPE_OF_REHAB.AMBULANT_MOBIL,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.static,
      value: TYPE_OF_REHAB.STATIONARY,
    },
  ];

  const isRequired =
    recommendedRehaType === TYPE_OF_REHAB.AMBULANT_MOBIL ||
    recommendedRehaType === TYPE_OF_REHAB.STATIONARY;

  return (
    <>
      <ControlledRadioGroup
        isRequired={!skipRequiredValidation}
        label={translations.patientForms.medicalForm.sectionFive.typeOfRehab}
        name="recommended_reha_type"
        options={options}
      />
      {isRequired && (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName="recommended_reha_type_reason"
          isIconOnly
          setSourceSelected={setSourceSelected}
          sourceSelected={sourceSelected}
        >
          <ControlledTextarea
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionTwo
                .complicationsDescriptionLabel
            }
            name="recommended_reha_type_reason"
          />
        </SourceWrapper>
      )}
    </>
  );
};

export const RehabDirectiveStateField = ({
  auction,
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const rehabDirectiveState = useWatch({ name: "rehab_directive_state" });

  useEffect(() => {
    if (rehabDirectiveState !== PREDICAMENT_STATE_YES) {
      setValue("rehab_directive_type", null);
    }
  }, [rehabDirectiveState, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <RehabDirectiveTypeSubForm
          auction={auction}
          setSourceSelected={setSourceSelected}
          skipRequiredValidation={skipRequiredValidation}
          sourceSelected={sourceSelected}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      isRequired={!skipRequiredValidation}
      label={translations.patientForms.medicalForm.sectionFive.rehabRl}
      name="rehab_directive_state"
      options={options}
    />
  );
};

const RehabDirectiveTypeSubForm = ({
  auction: _auction,
  setSourceSelected: _setSourceSelected,
  skipRequiredValidation,
  sourceSelected: _sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const options = [
    {
      label:
        translations.patientForms.medicalForm.sectionFive.myocardialInfarction,
      value: REHAB_DIRECTIVE_TYPE.MYOCARDIAL_INFARCTION,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.bypass,
      value: REHAB_DIRECTIVE_TYPE.BYPASS_WITH_VALVE_REPLACEMENT,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.heartFailure,
      value: REHAB_DIRECTIVE_TYPE.HEART_FAILURE_NYHA_II_OR_HIGHER,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive
          .endoprostheticTreatment,
      value: REHAB_DIRECTIVE_TYPE.ENDOPROSTHETIC_KNEE_OR_HIP,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.backPain,
      value: REHAB_DIRECTIVE_TYPE.CHRONIC_BACK_PAIN,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.spinalCanal,
      value: REHAB_DIRECTIVE_TYPE.SPINAL_STENOSIS,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.amputation,
      value: REHAB_DIRECTIVE_TYPE.MAJOR_LEG_AMPUTATION,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.respiratoryDisease,
      value: REHAB_DIRECTIVE_TYPE.RESPIRATORY_DISEASES,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.neurologicalDiseases,
      value: REHAB_DIRECTIVE_TYPE.NEUROLOGICAL_DISEASE_PHASE_D,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.oncologicalDisease,
      value: REHAB_DIRECTIVE_TYPE.ONCOLOGICAL_DISEASES,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.transplants,
      value: REHAB_DIRECTIVE_TYPE.ORGAN_TRANSPLANTS,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.geriatricRehab,
      value: REHAB_DIRECTIVE_TYPE.GERIATRIC_REHAB,
    },
  ];

  return (
    <ControlledCheckboxGroup
      aria-label={
        translations.patientForms.medicalForm.sectionFive
          .rehabDirectiveTypeLabel
      }
      isRequired={!skipRequiredValidation}
      name="rehab_directive_type"
      options={options}
    />
  );
};

export const SpecialRequirementsRequiredField = ({
  auction,
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const specialRequirementsRequired = useWatch({
    name: "special_requirements_required",
  });

  useEffect(() => {
    if (specialRequirementsRequired !== PREDICAMENT_STATE_YES) {
      setValue("special_requirements", null);
      Object.values(SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS).forEach(
        (fieldName) => {
          setValue(fieldName as keyof GKVMedicalFormData, "");
        },
      );
    }
  }, [specialRequirementsRequired, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <SpecialRequirementsSubForm
          auction={auction}
          setSourceSelected={setSourceSelected}
          skipRequiredValidation={skipRequiredValidation}
          sourceSelected={sourceSelected}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      isRequired={!skipRequiredValidation}
      label={
        translations.patientForms.medicalForm.sectionSix.specialRequirements
      }
      name="special_requirements_required"
      options={options}
    />
  );
};

const SpecialRequirementsSubForm = ({
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const specialRequirements = useWatch({ name: "special_requirements" });

  useEffect(() => {
    if (!Array.isArray(specialRequirements)) return;

    Object.entries(SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS).forEach(
      ([value, fieldName]) => {
        if (!specialRequirements.includes(Number(value))) {
          setValue(fieldName as keyof GKVMedicalFormData, "");
        }
      },
    );
  }, [specialRequirements, setValue]);

  const options = [
    {
      label: translations.patientForms.medicalForm.sectionSix.chemotherapy,
      value: SPECIAL_REQUIREMENTS.CHEMOTHERAPY,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionSix.immunosuppressants,
      value: SPECIAL_REQUIREMENTS.IMMUNOSUPPRESSANTS,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.catheter,
      value: SPECIAL_REQUIREMENTS.CATHETER,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.peg,
      value: SPECIAL_REQUIREMENTS.PEG,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.isolation,
      value: SPECIAL_REQUIREMENTS.ISOLATION,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.ventilation,
      value: SPECIAL_REQUIREMENTS.VENTILATION,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.tracheostoma,
      value: SPECIAL_REQUIREMENTS.TRACHEOSTOMA,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.weaning,
      value: SPECIAL_REQUIREMENTS.WEANING,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.prosthesis,
      value: SPECIAL_REQUIREMENTS.PROSTHESIS,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.overlengthBed,
      value: SPECIAL_REQUIREMENTS.OVERLENGTH_BED,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.obesity,
      value: SPECIAL_REQUIREMENTS.OBESITY,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.impairment,
      value: SPECIAL_REQUIREMENTS.IMPAIRMENT,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.relatives,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName={
            SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
              SPECIAL_REQUIREMENTS.RELATIVES
            ] as keyof GKVMedicalFormData
          }
          isIconOnly
          setSourceSelected={setSourceSelected}
          sourceSelected={sourceSelected}
        >
          <ControlledTextarea
            label={
              translations.patientForms.medicalForm.sectionFive
                .otherTextfieldLabel
            }
            name={
              SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
                SPECIAL_REQUIREMENTS.RELATIVES
              ] ?? ""
            }
          />
        </SourceWrapper>
      ),
      value: SPECIAL_REQUIREMENTS.RELATIVES,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.dialysis,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName={
            SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
              SPECIAL_REQUIREMENTS.DIALYSIS
            ] as keyof GKVMedicalFormData
          }
          isIconOnly
          setSourceSelected={setSourceSelected}
          sourceSelected={sourceSelected}
        >
          <ControlledTextarea
            label={
              translations.patientForms.medicalForm.sectionFive
                .otherTextfieldLabel
            }
            name={
              SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
                SPECIAL_REQUIREMENTS.DIALYSIS
              ] ?? ""
            }
          />
        </SourceWrapper>
      ),
      value: SPECIAL_REQUIREMENTS.DIALYSIS,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.decubitus,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName={
            SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
              SPECIAL_REQUIREMENTS.DECUBITUS
            ] as keyof GKVMedicalFormData
          }
          isIconOnly
          setSourceSelected={setSourceSelected}
          sourceSelected={sourceSelected}
        >
          <ControlledTextarea
            label={
              translations.patientForms.medicalForm.sectionFive
                .otherTextfieldLabel
            }
            name={
              SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
                SPECIAL_REQUIREMENTS.DECUBITUS
              ] ?? ""
            }
          />
        </SourceWrapper>
      ),
      value: SPECIAL_REQUIREMENTS.DECUBITUS,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.weightBed,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName={
            SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
              SPECIAL_REQUIREMENTS.WEIGHT_BED
            ] as keyof GKVMedicalFormData
          }
          isIconOnly
          setSourceSelected={setSourceSelected}
          sourceSelected={sourceSelected}
        >
          <ControlledInput
            name={
              SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
                SPECIAL_REQUIREMENTS.WEIGHT_BED
              ] ?? ""
            }
            placeholder=" "
            type="number"
          />
        </SourceWrapper>
      ),
      value: SPECIAL_REQUIREMENTS.WEIGHT_BED,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.other,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName={
            SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
              SPECIAL_REQUIREMENTS.OTHER
            ] as keyof GKVMedicalFormData
          }
          isIconOnly
          setSourceSelected={setSourceSelected}
          sourceSelected={sourceSelected}
        >
          <ControlledTextarea
            label={
              translations.patientForms.medicalForm.sectionFive
                .otherTextfieldLabel
            }
            name={
              SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
                SPECIAL_REQUIREMENTS.OTHER
              ] ?? ""
            }
          />
        </SourceWrapper>
      ),
      value: SPECIAL_REQUIREMENTS.OTHER,
    },
  ];

  return (
    <ControlledCheckboxGroup
      aria-label={
        translations.patientForms.medicalForm.sectionSix
          .specialRequirementsSelectOptionsLabel
      }
      isRequired={!skipRequiredValidation}
      name="special_requirements"
      options={options}
    />
  );
};

export const TransportTypeField = ({
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const transportType = useWatch({ name: "transport_type" });

  useEffect(() => {
    if (transportType !== TRANSPORTATION_TYPE.TAXI) {
      setValue("transport_type_taxi_specification", null);
    }
    if (transportType !== TRANSPORTATION_TYPE.AMBULANCE) {
      setValue("transport_type_ambulance_specification", null);
      setValue("transport_type_ambulance_description", "");
    }
  }, [transportType, setValue]);

  const taxiOptions = [
    {
      label: translations.patientForms.medicalForm.sectionSix.wheelChair,
      value: TRANSPORTATION_SUBTYPE.WHEELCHAIR,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.stretcher,
      value: TRANSPORTATION_SUBTYPE.STRETCHER,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.lyingDown,
      value: TRANSPORTATION_SUBTYPE.LYING_DOWN,
    },
  ];

  const options = [
    {
      label:
        translations.patientForms.medicalForm.sectionSix.optionPublicTransport,
      value: TRANSPORTATION_TYPE.PUBLIC,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.car,
      value: TRANSPORTATION_TYPE.CAR,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.taxi,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName="transport_type_taxi_specification"
          isIconOnly
          setSourceSelected={setSourceSelected}
          sourceSelected={sourceSelected}
        >
          <ControlledRadioGroup
            aria-label={
              translations.patientForms.medicalForm.sectionSix.taxiOptionLabel
            }
            isRequired={!skipRequiredValidation}
            name="transport_type_taxi_specification"
            options={taxiOptions}
          />
        </SourceWrapper>
      ),
      value: TRANSPORTATION_TYPE.TAXI,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.ambulance,
      subForm: () => (
        <>
          <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
            fieldName="transport_type_ambulance_specification"
            isIconOnly
            setSourceSelected={setSourceSelected}
            sourceSelected={sourceSelected}
          >
            <ControlledRadioGroup
              aria-label={
                translations.patientForms.medicalForm.sectionSix.taxiOptionLabel
              }
              isRequired={!skipRequiredValidation}
              name="transport_type_ambulance_specification"
              options={taxiOptions}
            />
          </SourceWrapper>
          <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
            fieldName="transport_type_ambulance_description"
            isIconOnly
            setSourceSelected={setSourceSelected}
            sourceSelected={sourceSelected}
          >
            <ControlledTextarea
              label={
                translations.patientForms.medicalForm.sectionSix
                  .ambulanceDescriptionLabel
              }
              name="transport_type_ambulance_description"
            />
          </SourceWrapper>
        </>
      ),
      value: TRANSPORTATION_TYPE.AMBULANCE,
    },
  ];

  return (
    <ControlledRadioGroup
      isRequired={!skipRequiredValidation}
      label={translations.patientForms.medicalForm.sectionSix.transportation}
      name="transport_type"
      options={options}
    />
  );
};

function getInitialICDCodeFormCount(
  formData: Partial<GKVMedicalFormData> | undefined,
): number {
  if (!formData) return FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_1;

  let maxFilledIndex = 0;

  Object.values(FUNCTION_DIAGNOSIS_PRIORITIES).forEach((priority) => {
    const diagnosis =
      formData[`icd_code_${priority}_diagnosis` as keyof GKVMedicalFormData];
    const code =
      formData[`icd_code_${priority}_code` as keyof GKVMedicalFormData];
    const reason =
      formData[`icd_code_${priority}_reason` as keyof GKVMedicalFormData];

    if (diagnosis || code || reason) {
      maxFilledIndex = priority;
    }
  });

  return Math.max(maxFilledIndex, FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_1);
}

export const ICDCodeFields = ({
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const icdReasonOptions = useICDReasonOptions();
  const { setValue } = useFormContext<GKVMedicalFormData>();

  const formData = useWatch<GKVMedicalFormData>();

  const {
    canDecrement,
    canIncrement,
    count: visibleCount,
    decrement,
    increment,
  } = useVisibleCount({
    initialCount: getInitialICDCodeFormCount(formData),
    maxCount: FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_5,
    minCount: FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_1,
  });

  useEffect(() => {
    for (let i = visibleCount; i <= FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_5; i++) {
      setValue(`icd_code_${i}_diagnosis` as keyof GKVMedicalFormData, "", {
        shouldDirty: true,
      });
      setValue(`icd_code_${i}_code` as keyof GKVMedicalFormData, "", {
        shouldDirty: true,
      });
      setValue(`icd_code_${i}_reason` as keyof GKVMedicalFormData, null, {
        shouldDirty: true,
      });
    }
  }, [visibleCount, setValue]);

  return (
    <div className="flex w-full flex-col gap-2">
      {Object.values(FUNCTION_DIAGNOSIS_PRIORITIES).map((priority) => {
        return (
          <div
            key={priority}
            className={clsx("flex w-full flex-col gap-2", {
              hidden: priority > visibleCount,
            })}
          >
            <p className="text-body-md font-bold">{priority}.</p>
            <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
              fieldName={`icd_code_${priority}_diagnosis`}
              setSourceSelected={setSourceSelected}
              sourceSelected={sourceSelected}
            >
              <ControlledInput
                isRequired={priority === 1 && !skipRequiredValidation}
                label={
                  translations.patientForms.medicalForm.sectionTwo
                    .diagnosisDescription
                }
                name={`icd_code_${priority}_diagnosis`}
                placeholder=" "
              />
            </SourceWrapper>
            <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
              fieldName={`icd_code_${priority}_code`}
              setSourceSelected={setSourceSelected}
              sourceSelected={sourceSelected}
            >
              <ControlledInput
                description={
                  translations.patientForms.medicalForm.sectionTwo.footnoteOne
                }
                isRequired={priority === 1 && !skipRequiredValidation}
                label={translations.patientForms.medicalForm.sectionTwo.icdCode}
                maxLength={ICD_CODE_CHARACTER_LIMIT}
                name={`icd_code_${priority}_code`}
                placeholder=" "
              />
            </SourceWrapper>
            <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
              fieldName={`icd_code_${priority}_reason`}
              setSourceSelected={setSourceSelected}
              sourceSelected={sourceSelected}
            >
              <ControlledSelect
                isRequired={priority === 1 && !skipRequiredValidation}
                items={icdReasonOptions}
                label={translations.patientForms.medicalForm.sectionTwo.cause}
                name={`icd_code_${priority}_reason`}
                renderItem={(item) => item.label}
                required={priority === 1 && !skipRequiredValidation}
              />
            </SourceWrapper>
            {priority >= visibleCount && (
              <div className="flex w-full justify-between">
                <Button
                  color="danger"
                  id={`delete-priority-${priority}`}
                  isDisabled={!canDecrement}
                  startContent={<TrashIcon className="h-4 w-4" />}
                  variant="light"
                  onPress={decrement}
                >
                  Delete (translation)
                </Button>
                <Button
                  color="primary"
                  id={`add-more-priority-${priority}`}
                  isDisabled={!canIncrement}
                  startContent={<PlusIcon className="h-4 w-4" />}
                  variant="ghost"
                  onPress={increment}
                >
                  Add more (translation)
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const DEFAULT_VISIBLE_OPERATION_ROWS = 1;

function getInitialOperationFormCount(
  formData: GKVMedicalFormData | undefined,
): number {
  let maxFilledIndex = DEFAULT_VISIBLE_OPERATION_ROWS;

  Object.values(OPERATION_INDEXES).forEach((opsIndex) => {
    const date =
      formData?.[`operation_${opsIndex}_date` as keyof GKVMedicalFormData];

    const ops = (
      formData?.[`operation_${opsIndex}_ops` as keyof GKVMedicalFormData] as
        | string[]
        | undefined
    )?.some((op) => op !== "");

    const woundClosed =
      formData?.[
        `operation_${opsIndex}_wound_closed` as keyof GKVMedicalFormData
      ];

    if (date || woundClosed || ops) {
      maxFilledIndex = opsIndex;
    }
  });

  return Math.max(maxFilledIndex, DEFAULT_VISIBLE_OPERATION_ROWS);
}

function getInitialOperationRows(fieldValue: string[] | undefined): number {
  let maxFilledIndex = DEFAULT_VISIBLE_OPERATION_ROWS;

  fieldValue?.forEach((op, index) => {
    if (op !== "") {
      maxFilledIndex = index + 1;
    }
  });

  return Math.max(maxFilledIndex, DEFAULT_VISIBLE_OPERATION_ROWS);
}

function OpsFields({
  opsIndex,
  prefix,
  setSourceSelected,
  shouldClearValue,
  sourceSelected,
  translations,
}: {
  opsIndex: number;
  shouldClearValue: boolean;
  prefix: string;
  setSourceSelected: FormFieldConfigProps<
    typeof DOCS_FORM_NAMES.GKV_MEDICAL
  >["setSourceSelected"];
  sourceSelected: keyof GKVMedicalFormData | undefined;
  translations: Translations;
}) {
  const fieldName = `${prefix}_ops` as keyof GKVMedicalFormData;

  const { formState, setValue } = useFormContext<GKVMedicalFormData>();
  const fieldValue = useWatch<GKVMedicalFormData>({ name: fieldName });
  const [ops, setOps] = useState<string[]>(
    (fieldValue ?? DEFAULT_OPS) as string[],
  );

  const { canDecrement, canIncrement, count, decrement, increment } =
    useVisibleCount({
      initialCount: getInitialOperationRows(
        formState.defaultValues?.[fieldName] as string[] | undefined,
      ),
      maxCount: NUMBER_OF_OPS_FIELDS,
      minCount: DEFAULT_VISIBLE_OPERATION_ROWS,
    });

  useEffect(() => {
    setValue(fieldName, ops, {
      shouldDirty: true,
    });
  }, [ops, setValue, fieldName]);

  useEffect(() => {
    setOps((prev) => {
      const newOps = [...prev];
      for (let i = 0; i < newOps.length; i++) {
        if (i >= count) {
          newOps[i] = "";
        }
      }
      return newOps;
    });
  }, [count, setOps]);

  useEffect(() => {
    if (shouldClearValue) {
      setValue(fieldName, DEFAULT_OPS, {
        shouldDirty: true,
      });
      setOps(DEFAULT_OPS);
    }
  }, [shouldClearValue, setValue, fieldName, setOps]);

  return (
    <div className="flex flex-col gap-2">
      <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
        fieldName={fieldName}
        isIconOnly
        setSourceSelected={setSourceSelected}
        sourceSelected={sourceSelected}
      >
        <div className="flex flex-col gap-2">
          {DEFAULT_OPS.map((_, index) => {
            const opsFieldIndex = index + 1;
            return (
              <div
                key={opsFieldIndex}
                className={clsx("w-full", {
                  hidden: opsFieldIndex > count,
                })}
              >
                <Input
                  label={`${translations.patientForms.medicalForm.sectionTwo.surgeryOps} ${opsFieldIndex}`}
                  placeholder=" "
                  value={ops[index]}
                  onValueChange={(value) => {
                    setOps((prev) => {
                      const newOps = [...prev];
                      newOps[index] = value;
                      return newOps;
                    });
                  }}
                />
              </div>
            );
          })}
        </div>
      </SourceWrapper>
      <div className="flex justify-between">
        <Button
          color="danger"
          id={`operation-${opsIndex}-remove-ops-code-field`}
          isDisabled={!canDecrement}
          startContent={<TrashIcon className="h-4 w-4" />}
          variant="light"
          onPress={decrement}
        >
          Remove (translation)
        </Button>
        <Button
          color="primary"
          id={`operation-${opsIndex}-add-ops-code-field`}
          isDisabled={!canIncrement}
          startContent={<PlusIcon className="h-4 w-4" />}
          variant="ghost"
          onPress={increment}
        >
          {translations.patientForms.medicalForm.sectionTwo.addOpsCode}
        </Button>
      </div>
    </div>
  );
}

function OperationsSubForm({
  setSourceSelected,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) {
  const { formState, setValue } = useFormContext<GKVMedicalFormData>();
  const surgeryPerformed = useWatch<GKVMedicalFormData>({
    name: "surgery_performed",
  });

  const { canDecrement, canIncrement, count, decrement, increment } =
    useVisibleCount({
      initialCount: getInitialOperationFormCount(
        formState.defaultValues as GKVMedicalFormData | undefined,
      ),
      maxCount: OPERATION_INDEXES.OP5,
      minCount: DEFAULT_VISIBLE_OPERATION_ROWS,
    });

  useEffect(() => {
    for (let i = count; i <= OPERATION_INDEXES.OP5; i++) {
      setValue(`operation_${count}_date` as keyof GKVMedicalFormData, null, {
        shouldDirty: true,
      });
      setValue(
        `operation_${count}_wound_closed` as keyof GKVMedicalFormData,
        null,
        {
          shouldDirty: true,
        },
      );
    }
  }, [count, setValue]);

  return (
    <div className="flex w-full flex-col">
      {Object.values(OPERATION_INDEXES).map((opsIndex) => {
        const prefix = `operation_${opsIndex}`;
        return (
          <div
            key={opsIndex}
            className={clsx("flex w-full flex-col gap-2", {
              hidden: opsIndex > count,
            })}
          >
            <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
              fieldName={`${prefix}_date` as keyof GKVMedicalFormData}
              isIconOnly
              setSourceSelected={setSourceSelected}
              sourceSelected={sourceSelected}
            >
              <ControlledDateInput
                label={
                  translations.patientForms.medicalForm.sectionTwo.surgeryDate
                }
                name={`${prefix}_date`}
              />
            </SourceWrapper>
            <OpsFields
              opsIndex={opsIndex}
              prefix={prefix}
              setSourceSelected={setSourceSelected}
              shouldClearValue={
                surgeryPerformed !== PREDICAMENT_STATE_YES || opsIndex > count
              }
              sourceSelected={sourceSelected}
              translations={translations}
            />
            <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
              fieldName={`${prefix}_wound_closed` as keyof GKVMedicalFormData}
              isIconOnly
              setSourceSelected={setSourceSelected}
              sourceSelected={sourceSelected}
            >
              <ControlledRadioGroup
                label={translations.patientForms.medicalForm.sectionTwo.wound}
                name={`${prefix}_wound_closed`}
                options={[
                  {
                    label: translations.actions.no,
                    value: PREDICAMENT_STATE_NO,
                  },
                  {
                    label: translations.actions.yes,
                    value: PREDICAMENT_STATE_YES,
                  },
                ]}
              />
            </SourceWrapper>
          </div>
        );
      })}
      <div className="flex justify-between">
        <Button
          color="danger"
          id="remove-operation-field"
          isDisabled={!canDecrement}
          startContent={<TrashIcon className="h-4 w-4" />}
          variant="light"
          onPress={decrement}
        >
          Remove (translation)
        </Button>
        <Button
          color="primary"
          id="add-operation-field"
          isDisabled={!canIncrement}
          startContent={<PlusIcon className="h-4 w-4" />}
          variant="ghost"
          onPress={increment}
        >
          {translations.patientForms.medicalForm.sectionTwo.addAnotherOperation}
        </Button>
      </div>
    </div>
  );
}

export const SurgeryPerformedField = ({
  setSourceSelected,
  skipRequiredValidation,
  sourceSelected,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const surgeryPerformed = useWatch<GKVMedicalFormData>({
    name: "surgery_performed",
  });

  useEffect(() => {
    if (surgeryPerformed !== PREDICAMENT_STATE_YES) {
      Object.values(OPERATION_INDEXES).forEach((opsIndex) => {
        const prefix = `operation_${opsIndex}`;
        setValue(`${prefix}_date` as keyof GKVMedicalFormData, null, {
          shouldDirty: true,
        });
        setValue(`${prefix}_wound_closed` as keyof GKVMedicalFormData, null, {
          shouldDirty: true,
        });
        setValue(`${prefix}_ops` as keyof GKVMedicalFormData, DEFAULT_OPS, {
          shouldDirty: true,
        });
      });
    }
  }, [surgeryPerformed, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <OperationsSubForm
          setSourceSelected={setSourceSelected}
          skipRequiredValidation={skipRequiredValidation}
          sourceSelected={sourceSelected}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      isRequired={!skipRequiredValidation}
      label={
        translations.patientForms.medicalForm.sectionTwo.performedTreatments
      }
      name="surgery_performed"
      options={options}
    />
  );
};
